import { GearPal } from "../../types/Pal";
import apiClient, { formDataClient } from "./client";

// Interfaccia per i filtri GearPal
export interface GearPalFilters {
  types?: string[];
  zipCode?: string;
  searchRadius?: number;
  category?: string;
  subcategory?: string;
  type?: string;
  off?: boolean;
}

export interface CreateGearPalData {
  name: string;
  zipcode: string;
  city: string;
  region: string;
  country: string;
  contacts: {
    type: string;
    value: string;
  }[];
  coordinates: {
    lat: number;
    lng: number;
  };
  items: {
    name: string;
    categoryId: string;
    externalLink?: string;
  }[];
}

export interface UpdateGearPalData {
  name?: string;
  zipcode?: string;
  city?: string;
  region?: string;
  country?: string;
  contacts?: {
    type: string;
    value: string;
  }[];
  coordinates?: {
    lat: number;
    lng: number;
  };
  items?: {
    name: string;
    categoryId: string;
    externalLink?: string;
  }[];
}

export const createGearpal = async (
  data: CreateGearPalData | FormData,
  language: string
) => {
  const user = localStorage.getItem("user-storage");
  const parsedUser = user ? JSON.parse(user) : null;
  const headers: any =
    parsedUser && parsedUser?.state?.user?.access_token
      ? {
          Authorization: `Bearer ${parsedUser.state.user.access_token}`,
          "Accept-Language": language,
        }
      : {
          "Accept-Language": language,
        };
  // Non impostare Content-Type per FormData, il browser lo farà automaticamente con il boundary
  if (data instanceof FormData) {
    // Rimuovi Content-Type per permettere al browser di impostare il boundary corretto
    delete headers["Content-Type"];
  } else {
    headers["Content-Type"] = "application/json";
  }
  const response = await apiClient.post("/gearpal/create-gearpal", data, {
    headers: headers,
  });
  return response.data;
};

export const getAllGearpals = async (
  language: string,
  filters?: GearPalFilters
): Promise<GearPal[]> => {
  const params = new URLSearchParams();

  if (filters?.zipCode) params.append("zipCode", filters.zipCode);
  if (filters?.searchRadius)
    params.append("radius", filters.searchRadius.toString());
  if (filters?.category) params.append("category", filters.category);
  if (filters?.subcategory) params.append("subcategory", filters.subcategory);
  if (filters?.type) params.append("type", filters.type);
  if (filters?.off) params.append("off", "true");

  const queryString = params.toString();
  const url = queryString
    ? `/gearpal/get-all-gearpals?${queryString}`
    : "/gearpal/get-all-gearpals";

  const response = await apiClient.get(url, {
    headers: {
      "Accept-Language": language,
    },
  });
  return response.data.result;
};

export const getMyGearpals = async (language: string) => {
  const user = localStorage.getItem("user-storage");
  const parsedUser = user ? JSON.parse(user) : null;
  const headers =
    parsedUser && parsedUser?.state?.user?.access_token
      ? {
          Authorization: `Bearer ${parsedUser.state.user.access_token}`,
          "Accept-Language": language,
        }
      : {
          "Accept-Language": language,
        };
  const response = await apiClient.get("/gearpal/my-gearpals", {
    headers: headers,
  });
  return response.data.result;
};

export const deleteGearpal = async (id: number, language: string) => {
  const user = localStorage.getItem("user-storage");
  const parsedUser = user ? JSON.parse(user) : null;
  const headers =
    parsedUser && parsedUser?.state?.user?.access_token
      ? {
          Authorization: `Bearer ${parsedUser.state.user.access_token}`,
          "Accept-Language": language,
        }
      : {
          "Accept-Language": language,
        };
  const response = await apiClient.post(
    "/gearpal/delete-gearpal",
    { id },
    {
      headers: headers,
    }
  );
  return response.data;
};

export const updateGearPal = async (
  id: number,
  data: UpdateGearPalData | FormData,
  language: string
) => {
  const user = localStorage.getItem("user-storage");
  const parsedUser = user ? JSON.parse(user) : null;

  if (!parsedUser?.state?.user?.access_token) {
    throw new Error("Token di accesso non trovato. Effettua il login.");
  }

  const headers: any = {
    Authorization: `Bearer ${parsedUser.state.user.access_token}`,
    "Accept-Language": language,
  };

  // Non impostare Content-Type per FormData, il browser lo farà automaticamente con il boundary
  if (data instanceof FormData) {
    // Rimuovi Content-Type per permettere al browser di impostare il boundary corretto
    delete headers["Content-Type"];
  } else {
    headers["Content-Type"] = "application/json";
  }

  const response = await apiClient.patch(
    `/gearpal/update-gearpal/${id}`,
    data,
    {
      headers: headers,
    }
  );

  return response.data;
};

export const addGearItem = async (
  gearpalId: number,
  data: FormData,
  language: string
) => {
  const user = localStorage.getItem("user-storage");
  const parsedUser = user ? JSON.parse(user) : null;

  if (!parsedUser?.state?.user?.access_token) {
    throw new Error("Token di accesso non trovato. Effettua il login.");
  }

  const headers: any = {
    Authorization: `Bearer ${parsedUser.state.user.access_token}`,
    "Accept-Language": language,
  };

  // FormData content type should be handled by browser
  if (data instanceof FormData) {
    delete headers["Content-Type"];
  }

  const response = await formDataClient.post(`/item/${gearpalId}`, data, {
    headers: headers,
  });

  return response.data;
};

export const deleteGearItem = async (id: number, language: string) => {
  const user = localStorage.getItem("user-storage");
  const parsedUser = user ? JSON.parse(user) : null;
  const headers =
    parsedUser && parsedUser?.state?.user?.access_token
      ? {
          Authorization: `Bearer ${parsedUser.state.user.access_token}`,
          "Accept-Language": language,
        }
      : {
          "Accept-Language": language,
        };
  const response = await apiClient.delete(`/item/${id}`, {
    headers: headers,
  });
  return response.data;
};

export const updateGearItem = async (
  id: number,
  data: any,
  language: string
) => {
  const user = localStorage.getItem("user-storage");
  const parsedUser = user ? JSON.parse(user) : null;
  const headers: any =
    parsedUser && parsedUser?.state?.user?.access_token
      ? {
          Authorization: `Bearer ${parsedUser.state.user.access_token}`,
          "Accept-Language": language,
          "Content-Type": "application/json",
        }
      : {
          "Accept-Language": language,
          "Content-Type": "application/json",
        };

  const response = await apiClient.patch(`/item/${id}`, data, {
    headers: headers,
  });
  return response.data;
};

export const addGearItemImage = async (
  itemId: number,
  data: FormData,
  language: string
) => {
  const user = localStorage.getItem("user-storage");
  const parsedUser = user ? JSON.parse(user) : null;
  const headers: any =
    parsedUser && parsedUser?.state?.user?.access_token
      ? {
          Authorization: `Bearer ${parsedUser.state.user.access_token}`,
          "Accept-Language": language,
        }
      : {
          "Accept-Language": language,
        };

  const response = await formDataClient.post(`/item-images/${itemId}`, data, {
    headers: headers,
  });
  return response.data;
};

export const updateGearItemImage = async (
  imageId: number,
  data: FormData,
  language: string
) => {
  const user = localStorage.getItem("user-storage");
  const parsedUser = user ? JSON.parse(user) : null;
  const headers: any =
    parsedUser && parsedUser?.state?.user?.access_token
      ? {
          Authorization: `Bearer ${parsedUser.state.user.access_token}`,
          "Accept-Language": language,
        }
      : {
          "Accept-Language": language,
        };

  const response = await formDataClient.patch(`/item-images/${imageId}`, data, {
    headers: headers,
  });
  return response.data;
};

export const deleteGearItemImage = async (
  imageId: number,
  language: string
) => {
  const user = localStorage.getItem("user-storage");
  const parsedUser = user ? JSON.parse(user) : null;
  const headers: any =
    parsedUser && parsedUser?.state?.user?.access_token
      ? {
          Authorization: `Bearer ${parsedUser.state.user.access_token}`,
          "Accept-Language": language,
        }
      : {
          "Accept-Language": language,
        };

  const response = await apiClient.delete(`/item-images/${imageId}`, {
    headers: headers,
  });
  return response.data;
};
